import classNames from 'classnames';
import {TPropsWithClassName} from 'core/types';
import React, {memo, PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {TTypographyVariant, Typography} from 'components/Typography';
import parse from 'html-react-parser';

import style from './style.module.scss';

interface IProps {
    maxTextLength?: number;
    text: string;
    expandedButtonText?: string;
    collapsedButtonText?: string;
    onChange?: (isExpanded: boolean) => void;
    buttonTextVariant?: TTypographyVariant;
    typographyClassName?: string;
    isTextClickable?: boolean;
    hasExpandButton?: boolean;
}

const MAX_TEXT_LENGTH_DEFAULT = 206;
const SEPARATOR = '... ';

export const CollapseTextInline = memo<TPropsWithClassName<PropsWithChildren<IProps>>>(
    ({
        maxTextLength = MAX_TEXT_LENGTH_DEFAULT,
        typographyClassName,
        text,
        collapsedButtonText = 'подробнее',
        expandedButtonText = 'Свернуть',
        onChange,
        buttonTextVariant = 'badge-s',
        isTextClickable,
        hasExpandButton = true,
    }) => {
        const trimmedText = text.trim();

        const [contentText, setContentText] = useState(trimmedText.slice(0, maxTextLength));
        const [clamped, setClamped] = useState(true);

        const needCollapse = useMemo(() => {
            return trimmedText.length > maxTextLength;
        }, [maxTextLength, trimmedText.length]);

        useEffect(() => {
            if (clamped) {
                setContentText(trimmedText.slice(0, maxTextLength));
            } else {
                setContentText(trimmedText);
            }
        }, [clamped, trimmedText, maxTextLength]);

        const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            const isExpanded = !clamped;
            setClamped(isExpanded);
            onChange?.(isExpanded);
        };

        const handleTextClick = (e: React.MouseEvent) => {
            if (isTextClickable) {
                e.stopPropagation();
                e.preventDefault();
                handleClick(e);
            }
        };

        return (
            <>
                <span onClick={handleTextClick} title={text}>
                    {parse(contentText)}
                </span>
                {needCollapse && clamped ? SEPARATOR : ' '}
                {needCollapse && hasExpandButton && (
                    <Typography
                        className={classNames(style.button, 'collapse-text-button', typographyClassName)}
                        onClick={handleClick}
                        variant={buttonTextVariant}
                    >
                        {clamped ? collapsedButtonText : expandedButtonText}
                    </Typography>
                )}
            </>
        );
    },
);
